import { isHandsFreeDelivery } from './shared-responder-v2-core-handsfree.selectors';
import {
  computeVisibleUpperBound,
  hasBranchingBarrier,
} from './shared-responder-v2-core-reachability.helpers';
import {
  RESPONDER_PHASES,
  RESPONSE_MODES,
  type ResponderPhase,
} from './shared-responder-v2-core-session.consts';
import {
  hasInFlightThread,
  selectCurrentSpec,
  selectCurrentThread,
  selectEffectiveMode,
  selectIsHostPageSite,
  selectIsStepAnswered,
  selectMobileAppEmbedUrl,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { isSiteVisitInProgress } from './shared-responder-v2-core-site-gate.helpers';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';

export interface ResponderHostHeaderModel {
  readonly phase: ResponderPhase;
  readonly stepNumber: number;
  readonly stepCount: number | null;
  readonly progressRatio: number;
  readonly previousStepIndex: number | null;
  readonly canGoBack: boolean;
  readonly canReturnToIntro: boolean;
  readonly canReturnToTask: boolean;
}

function isCaptureFreeSession(state: ResponderSessionState): boolean {
  return (
    !state.capabilities.hasUrlOrTask &&
    !isHandsFreeDelivery(state) &&
    selectEffectiveMode(state) === RESPONSE_MODES.Text
  );
}

export function selectCanReturnToIntro(state: ResponderSessionState): boolean {
  return (
    state.phase === RESPONDER_PHASES.Question &&
    state.capabilities.hasIntro &&
    state.currentStepIndex === 0 &&
    state.editing.mode !== 'editing' &&
    !hasInFlightThread(state) &&
    isCaptureFreeSession(state)
  );
}

export function selectIsHostPageTaskInProgress(
  state: ResponderSessionState
): boolean {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  return (
    state.phase === RESPONDER_PHASES.Question &&
    spec !== null &&
    thread !== null &&
    getThreadBehaviour(spec.kind).ownsContentUrl &&
    selectIsHostPageSite(state) &&
    selectMobileAppEmbedUrl(state) === null &&
    isSiteVisitInProgress(thread)
  );
}

function selectPreviousAnsweredStepIndex(
  state: ResponderSessionState
): number | null {
  if (state.phase !== RESPONDER_PHASES.Question) {
    return null;
  }

  for (let index = state.currentStepIndex - 1; index >= 0; index -= 1) {
    const step = state.steps[index];

    if (step !== undefined && selectIsStepAnswered(state, step.questionId)) {
      return index;
    }
  }

  return null;
}

function resolveProgressRatio(
  state: ResponderSessionState,
  stepNumber: number,
  visibleTotal: number
): number {
  if (state.phase === RESPONDER_PHASES.Outro) {
    return 1;
  }

  if (state.phase !== RESPONDER_PHASES.Question || state.steps.length === 0) {
    return 0;
  }

  return Math.min(stepNumber / visibleTotal, 1);
}

export function selectHostHeaderModel(
  state: ResponderSessionState
): ResponderHostHeaderModel {
  const stepNumber = state.currentStepIndex + 1;
  const visibleTotal =
    computeVisibleUpperBound({
      steps: state.steps,
      maxReachedStepIndex: state.maxReachedStepIndex,
      isComplete: state.isComplete,
    }) + 1;
  const previousStepIndex = selectPreviousAnsweredStepIndex(state);
  const canReturnToIntro = selectCanReturnToIntro(state);

  return {
    phase: state.phase,
    stepNumber,
    stepCount: hasBranchingBarrier({
      steps: state.steps,
      maxReachedStepIndex: state.maxReachedStepIndex,
    })
      ? null
      : visibleTotal,
    progressRatio: resolveProgressRatio(state, stepNumber, visibleTotal),
    previousStepIndex,
    canGoBack:
      canReturnToIntro ||
      (previousStepIndex !== null &&
        state.editing.mode !== 'editing' &&
        !hasInFlightThread(state)),
    canReturnToIntro,
    canReturnToTask: selectIsHostPageTaskInProgress(state),
  };
}
